/**
 * Environmental music / ambient stems: mountain ascent by integrity.
 *
 *   music.foundation  → forest base (birds + stream) — at ease
 *   ambient.secret    → cliffside wind — medium pressure
 *   music.pressure    → volcanic crater — critical
 *
 * Crossfades on layer tilts (Mind → Heart → Will), not integrity free-fall,
 * so the bed tracks the board journey rather than only the end-game.
 */

import type { AudioBusGraph } from "./buses";
import type { ClipId } from "./catalog";
import {
  ambientAscentWeights,
  threatScore,
  type AudioFrameInput,
} from "./frame";
import type { AudioSettings } from "./settings";

export type BufferResolver = (id: ClipId) => AudioBuffer | null;

const FOREST: ClipId = "music.foundation";
const CLIFF: ClipId = "ambient.secret";
const VOLCANO: ClipId = "music.pressure";

/** Stem peak gains — environmental beds should sit under SFX but be clearly heard. */
const FOREST_GAIN = 0.85;
const CLIFF_GAIN = 0.8;
const VOLCANO_GAIN = 0.88;

/**
 * Music / ambient director — integrity-driven environmental journey.
 */
export class MusicDirector {
  private readonly context: AudioContext;
  private resolveBuffer: BufferResolver;

  private contentEnabled = false;
  private started = false;
  private disposed = false;

  private forestSrc: AudioBufferSourceNode | null = null;
  private cliffSrc: AudioBufferSourceNode | null = null;
  private volcanoSrc: AudioBufferSourceNode | null = null;

  /** Buffer identity last used for forest loop — restart if a better buffer arrives. */
  private forestBuffer: AudioBuffer | null = null;

  private forestGain: GainNode;
  private cliffGain: GainNode;
  private volcanoGain: GainNode;

  private lastThreat = 0;
  private lastForestTarget = -1;
  private lastCliffTarget = -1;
  private lastVolcanoTarget = -1;
  private warnedMissing = false;
  /** Only warn about missing foundation after preload has run (not on boot). */
  private expectBuffers = false;

  constructor(
    context: AudioContext,
    graph: AudioBusGraph,
    resolveBuffer: BufferResolver,
  ) {
    this.context = context;
    this.resolveBuffer = resolveBuffer;

    this.forestGain = context.createGain();
    this.cliffGain = context.createGain();
    this.volcanoGain = context.createGain();
    this.forestGain.gain.value = 0;
    this.cliffGain.gain.value = 0;
    this.volcanoGain.gain.value = 0;

    this.forestGain.connect(graph.getBus("music"));
    this.volcanoGain.connect(graph.getBus("music"));
    this.cliffGain.connect(graph.getBus("ambient"));
  }

  setBufferResolver(fn: BufferResolver): void {
    this.resolveBuffer = fn;
  }

  setContentEnabled(enabled: boolean): void {
    this.contentEnabled = enabled;
    if (!enabled) {
      this.stopSources();
      this.zeroGains();
    } else if (this.expectBuffers) {
      // Only auto-start once preload has marked buffers expected
      this.tryStart();
    }
  }

  /** Call after music assets have been requested (preload finished). */
  markBuffersExpected(): void {
    this.expectBuffers = true;
  }

  isContentEnabled(): boolean {
    return this.contentEnabled;
  }

  isStarted(): boolean {
    return this.started;
  }

  getThreat(): number {
    return this.lastThreat;
  }

  /**
   * Call after AudioContext.resume() and after buffers are cached.
   * Restarts loops so suspended-context starts cannot leave the bed silent.
   */
  kick(): void {
    if (this.disposed || !this.contentEnabled) return;
    // Never start BufferSources while suspended — browsers drop them silently
    // and spam "AudioContext was not allowed to start" before a user gesture.
    if (this.context.state !== "running") return;
    this.restartSources();
    if (!this.started) return;
    this.applyGainsImmediate(FOREST_GAIN, 0, 0);
  }

  /**
   * Stop + start loops from current buffers (after preload / resume).
   */
  restartSources(): void {
    if (this.disposed || !this.contentEnabled) return;
    this.stopSources();
    this.tryStart();
  }

  tryStart(): void {
    if (this.disposed || !this.contentEnabled || this.started) return;
    // Avoid scheduling sources on a suspended context (autoplay policy)
    if (this.context.state !== "running") return;

    const forest = this.resolveBuffer(FOREST);
    const cliff = this.resolveBuffer(CLIFF);
    const volcano = this.resolveBuffer(VOLCANO);
    if (!forest) {
      if (this.expectBuffers && !this.warnedMissing) {
        this.warnedMissing = true;
        // eslint-disable-next-line no-console
        console.warn(
          "[audio] music.foundation not loaded — music bed silent (check /audio/music/foundation.mp3)",
        );
      }
      return;
    }

    this.forestBuffer = forest;
    this.forestSrc = this.startLoop(forest, this.forestGain);
    if (cliff) this.cliffSrc = this.startLoop(cliff, this.cliffGain);
    if (volcano) this.volcanoSrc = this.startLoop(volcano, this.volcanoGain);
    this.started = true;
    this.warnedMissing = false;
  }

  private startLoop(
    buffer: AudioBuffer,
    dest: GainNode,
  ): AudioBufferSourceNode {
    const src = this.context.createBufferSource();
    src.buffer = buffer;
    src.loop = true;
    src.connect(dest);
    try {
      // 0 = "now"; works when context is running or will run after resume
      src.start(0);
    } catch {
      /* already started or context closed */
    }
    return src;
  }

  tick(frame: AudioFrameInput, settings: AudioSettings): void {
    if (this.disposed) return;
    this.lastThreat = threatScore(frame);

    if (!this.contentEnabled) {
      this.zeroGains();
      return;
    }

    // Keep trying until foundation is available (preload may finish mid-session)
    if (!this.started) {
      this.tryStart();
    } else {
      // If a real buffer replaced a missing/synth early start, restart once
      const forest = this.resolveBuffer(FOREST);
      if (forest && forest !== this.forestBuffer) {
        this.restartSources();
      }
    }
    if (!this.started) return;

    const now = this.context.currentTime;
    // Mute only when user muted or mission over — keep bed during brief sim pauses
    // (reveal-hold) so music does not drop out constantly in Easy mode.
    const hardSilent =
      settings.muted || frame.status !== "playing";
    // Soft duck when user-paused (explicit pause), not reveal-hold freezes
    const softSilent = frame.paused;

    if (hardSilent) {
      this.ramp(this.forestGain.gain, 0, now, 0.35, "forest");
      this.ramp(this.cliffGain.gain, 0, now, 0.35, "cliff");
      this.ramp(this.volcanoGain.gain, 0, now, 0.35, "volcano");
      return;
    }

    // Journey follows layer collapse (Mind → Heart), not late integrity free-fall
    const w = ambientAscentWeights(frame.tilts);
    const mul = softSilent ? 0.35 : 1;

    this.ramp(
      this.forestGain.gain,
      w.forest * FOREST_GAIN * mul,
      now,
      softSilent ? 0.4 : 1.4,
      "forest",
    );
    this.ramp(
      this.cliffGain.gain,
      w.cliff * CLIFF_GAIN * mul,
      now,
      softSilent ? 0.4 : 1.6,
      "cliff",
    );
    this.ramp(
      this.volcanoGain.gain,
      w.volcano * VOLCANO_GAIN * mul,
      now,
      softSilent ? 0.4 : 1.2,
      "volcano",
    );
  }

  private applyGainsImmediate(
    forest: number,
    cliff: number,
    volcano: number,
  ): void {
    const now = this.context.currentTime;
    try {
      this.forestGain.gain.cancelScheduledValues(now);
      this.cliffGain.gain.cancelScheduledValues(now);
      this.volcanoGain.gain.cancelScheduledValues(now);
      this.forestGain.gain.setValueAtTime(Math.max(0, forest), now);
      this.cliffGain.gain.setValueAtTime(Math.max(0, cliff), now);
      this.volcanoGain.gain.setValueAtTime(Math.max(0, volcano), now);
    } catch {
      this.forestGain.gain.value = Math.max(0, forest);
      this.cliffGain.gain.value = Math.max(0, cliff);
      this.volcanoGain.gain.value = Math.max(0, volcano);
    }
    this.lastForestTarget = forest;
    this.lastCliffTarget = cliff;
    this.lastVolcanoTarget = volcano;
  }

  private ramp(
    param: AudioParam,
    target: number,
    now: number,
    timeConstant: number,
    which: "forest" | "cliff" | "volcano",
  ): void {
    const next = Math.max(0, target);
    const last =
      which === "forest"
        ? this.lastForestTarget
        : which === "cliff"
          ? this.lastCliffTarget
          : this.lastVolcanoTarget;
    if (Math.abs(next - last) < 0.012) return;
    if (which === "forest") this.lastForestTarget = next;
    else if (which === "cliff") this.lastCliffTarget = next;
    else this.lastVolcanoTarget = next;

    try {
      const current = param.value;
      param.cancelScheduledValues(now);
      // Jump from silence so the bed is heard immediately after unlock
      if (current < 0.05 && next > 0.05) {
        param.setValueAtTime(next * 0.92, now);
        param.setTargetAtTime(next, now, 0.08);
      } else if (next < 0.02) {
        param.setValueAtTime(current, now);
        param.setTargetAtTime(0, now, 0.12);
      } else {
        param.setValueAtTime(current, now);
        param.setTargetAtTime(next, now, Math.max(0.08, timeConstant) / 3);
      }
    } catch {
      param.value = next;
    }
  }

  private zeroGains(): void {
    this.applyGainsImmediate(0, 0, 0);
  }

  private stopSources(): void {
    for (const s of [this.forestSrc, this.cliffSrc, this.volcanoSrc]) {
      try {
        s?.stop();
        s?.disconnect();
      } catch {
        /* */
      }
    }
    this.forestSrc = null;
    this.cliffSrc = null;
    this.volcanoSrc = null;
    this.forestBuffer = null;
    this.started = false;
  }

  resetMission(): void {
    this.lastThreat = 0;
    if (this.contentEnabled) {
      this.kick();
    } else {
      this.lastForestTarget = -1;
      this.lastCliffTarget = -1;
      this.lastVolcanoTarget = -1;
    }
  }

  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.stopSources();
    try {
      this.forestGain.disconnect();
      this.cliffGain.disconnect();
      this.volcanoGain.disconnect();
    } catch {
      /* */
    }
  }
}
